import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, RotateCcw, AlertTriangle, CheckCircle2, History, Inbox, Clock, ExternalLink } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API = '/api';

const SAFETY_LABELS = { 1: 'Auto', 2: 'Affirm', 3: 'Button', 4: 'Typed' };
const SAFETY_COLORS = {
  1: 'text-emerald-600 dark:text-emerald-400',
  2: 'text-sky-600 dark:text-sky-400',
  3: 'text-amber-600 dark:text-amber-400',
  4: 'text-red-600 dark:text-red-400',
};

const STATUS_BADGE = {
  completed: { color: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900', icon: CheckCircle2 },
  failed:    { color: 'bg-red-100 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900', icon: AlertTriangle },
  undone:    { color: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700', icon: RotateCcw },
  executing: { color: 'bg-sky-100 text-sky-700 border-sky-300 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900', icon: Clock },
};

function formatTimeAgo(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diffSec = Math.floor((Date.now() - t) / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ''; }
}

function useUndoCountdown(deadlineIso) {
  const [secs, setSecs] = useState(() => {
    if (!deadlineIso) return null;
    return Math.max(0, Math.floor((new Date(deadlineIso).getTime() - Date.now()) / 1000));
  });
  useEffect(() => {
    if (!deadlineIso) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((new Date(deadlineIso).getTime() - Date.now()) / 1000));
      setSecs(remaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadlineIso]);
  return secs;
}

const ActionRow = ({ action, onUndo, undoing }) => {
  const statusKey = action.status || 'completed';
  const statusCfg = STATUS_BADGE[statusKey] || STATUS_BADGE.completed;
  const StatusIcon = statusCfg.icon;
  const sl = action.safety_level;
  const slColor = SAFETY_COLORS[sl] || 'text-muted-foreground';

  const undoSecs = useUndoCountdown(statusKey === 'completed' ? action.undo_deadline : null);
  const canUndo = statusKey === 'completed' && action.undo_deadline && undoSecs > 0;

  const diff = action.diff;
  const diffSummary = diff
    ? [
        diff.added && Object.keys(diff.added).length > 0 ? `${Object.keys(diff.added).length} added` : null,
        diff.removed && Object.keys(diff.removed).length > 0 ? `${Object.keys(diff.removed).length} removed` : null,
        diff.changed && Object.keys(diff.changed).length > 0 ? `${Object.keys(diff.changed).length} changed` : null,
      ].filter(Boolean).join(', ')
    : null;

  return (
    <div className="px-4 py-3 hover:bg-muted/30 transition-colors" data-testid={`action-row-${action.action_run_id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">{action.capability_id}</span>
            <Badge variant="outline" className={cn('text-[10px] gap-1 border', statusCfg.color)} data-testid={`action-status-${action.action_run_id}`}>
              <StatusIcon className="w-3 h-3" />
              {statusKey}
            </Badge>
            {sl != null && (
              <span className={cn('text-[10px] font-mono font-semibold', slColor)} title={`Safety Level ${sl} — ${SAFETY_LABELS[sl] || ''}`}>
                L{sl}
              </span>
            )}
            {action.source && (
              <span className="text-[9px] font-mono uppercase text-muted-foreground">{action.source}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
            <Clock className="w-3 h-3" />
            <span>{formatTimeAgo(action.created_at)}</span>
            {diffSummary && <span className="text-violet-600 dark:text-violet-400">· {diffSummary}</span>}
            {action.error && action.error.message && (
              <span className="text-red-600 dark:text-red-400 truncate">· {action.error.error_code || 'AI-TOOL'}: {action.error.message}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {canUndo && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-900 dark:text-amber-400 dark:hover:bg-amber-950/40"
              onClick={() => onUndo(action.action_run_id)}
              disabled={undoing === action.action_run_id}
              data-testid={`action-undo-${action.action_run_id}`}
            >
              {undoing === action.action_run_id ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RotateCcw className="w-3 h-3" />
              )}
              Undo · {undoSecs}s
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const RecentActionsPanel = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [undoing, setUndoing] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchActions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ai/v2/actions/recent?limit=50`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setActions(data.items || []);
      }
    } catch (e) {
      console.error('fetchActions error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh on mount + every 5s for live undo countdowns to stay accurate.
  useEffect(() => {
    fetchActions();
    const id = setInterval(fetchActions, 5000);
    return () => clearInterval(id);
  }, [fetchActions, refreshKey]);

  const handleUndo = async (actionRunId) => {
    setUndoing(actionRunId);
    try {
      const res = await fetch(`${API}/ai/v2/actions/${actionRunId}/undo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: '{}',
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Action undone', {
          description: data.message || 'Snapshot restored.',
          action: data.deck_item_id ? { label: 'Open Deck', onClick: () => { window.location.href = '/ai'; } } : undefined,
        });
        setRefreshKey(k => k + 1);
      } else {
        const detail = data.detail || data;
        toast.error(detail.error_code || 'AI-UNDO', { description: detail.message || 'Undo failed.' });
      }
    } catch (e) {
      toast.error('Undo failed', { description: String(e) });
    } finally {
      setUndoing(null);
    }
  };

  return (
    <Card data-testid="recent-actions-card">
      <CardHeader className="py-4 px-6 border-b">
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="w-4 h-4 text-violet-500" />
          Recent AI Actions
          <Badge variant="secondary" className="ml-2 text-[10px]" data-testid="recent-actions-count">
            {actions.length}
          </Badge>
        </CardTitle>
        <CardDescription>
          The last 50 capability executions through the v2 gateway. Completed actions can be undone within the 45s window.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading...
          </div>
        ) : actions.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No recent actions yet.</p>
            <p className="text-xs mt-1">When Orka runs a capability, it will appear here.</p>
          </div>
        ) : (
          <div className="divide-y">
            {actions.map(a => (
              <ActionRow
                key={a.action_run_id}
                action={a}
                onUndo={handleUndo}
                undoing={undoing}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentActionsPanel;
